import { useEffect, useMemo, useState } from "react";
import Modalstructure from "../../../components/structure";
import Modalheader from "../../../components/header";
import ModalBody from "../../../components/body";
import ModalFooter from "../../../components/footer";
import StyledInput from "../../../../styledInput/StyledInput";
import SelectPhone from "../../../../phone/SelectPhone";
import RangeDatePicker from "../../../../datePicker/RangeDatePicker";
import Button from "../../../../button";
import SubmitButton from "../../../../button/SubmitButton";
import { Plus, ChevronLeft } from "../../../../../assets/icons";
import ApiDatabase from "../../../../../server";
import { dateToYYYYMMDD, forceDateToUtc0 } from "../../../../../utils/Date";

const formatIntegrationSource = (source) => {
  if (source === "peopulse") return "Peopulse";
  if (source === "jobbiz") return "Jobbiz";
  if (source === "workfully") return "Workfully";
  return source ? String(source) : "—";
};

const formatSectorSourceLabel = (source) => formatIntegrationSource(source);

const workfullyMatricule = (worker) => {
  const row = (worker?.bigCompanyExternalIds || []).find(
    (x) => x.source === "workfully",
  );
  const raw = row?.externalId ?? row?.peopulseId;
  const id = raw != null ? String(raw).trim() : "";
  return id || null;
};

const isSourceLabelAbsent = (source) => {
  const label = formatSectorSourceLabel(source);
  return !source || label === "—";
};

const workerDisplayName = (worker) =>
  [worker?.firstname, worker?.lastname].filter(Boolean).join(" ").trim() ||
  workfullyMatricule(worker) ||
  "Employé";

const CONTRACT_TYPE_OPTIONS = [
  { value: "interim", label: "Intérim" },
  { value: "cdi", label: "CDI" },
  { value: "cdd", label: "CDD" },
  { value: "stage", label: "Stage" },
  { value: "alternance", label: "Alternance" },
];

const resolveWorkerDisplayMeta = (worker) => {
  const phoneRaw = worker.phone?.trim() || null;

  const wfId = workfullyMatricule(worker);
  if (wfId) {
    return {
      phone: phoneRaw,
      sourceLabel: "Workfully",
      matricule: wfId,
      missingPhone: !phoneRaw,
      missingRefLine: false,
    };
  }
  const ext = (worker.bigCompanyExternalIds || []).find((x) => {
    if (x.source === "workfully") return false;
    return Boolean(x.peopulseId || x.externalId);
  });
  if (ext) {
    const rawSrc = formatIntegrationSource(ext.source);
    const sourceLabel =
      ext.source && rawSrc !== "—" ? rawSrc : null;
    const matricule =
      String(ext.peopulseId || ext.externalId || "").trim() || null;
    return {
      phone: phoneRaw,
      sourceLabel,
      matricule,
      missingPhone: !phoneRaw,
      missingRefLine: !sourceLabel && !matricule,
    };
  }
  return {
    phone: phoneRaw,
    sourceLabel: null,
    matricule: null,
    missingPhone: !phoneRaw,
    missingRefLine: true,
  };
};

const workerRefLineText = (meta) => {
  if (meta.missingRefLine) return "Aucune source ni matricule";
  const left = meta.sourceLabel;
  const right = meta.matricule;
  if (left && right) return `${left} · ${right}`;
  if (left) return `${left} · non renseigné`;
  if (right) return `Source non renseignée · ${right}`;
  return "Aucune source ni matricule";
};

const WorkerSecondaryLines = ({ meta, hasSelection }) => {
  const phonePresent = !meta.missingPhone;
  const refComplete =
    !meta.missingRefLine && meta.sourceLabel && meta.matricule;

  const phoneTone = phonePresent
    ? hasSelection
      ? "text-white/85"
      : "text-gray-500"
    : hasSelection
      ? "text-white/55 italic"
      : "text-gray-400 italic";

  const refTone = refComplete
    ? hasSelection
      ? "text-white/85"
      : "text-gray-500"
    : hasSelection
      ? "text-white/55 italic"
      : "text-gray-400 italic";

  return (
    <>
      <div className={`text-xs ${phoneTone}`}>
        {phonePresent ? meta.phone : "Téléphone non renseigné"}
      </div>
      <div className={`text-xs ${refTone}`}>{workerRefLineText(meta)}</div>
    </>
  );
};

const WorkerOptionLabelNode = ({ worker }) => {
  const meta = resolveWorkerDisplayMeta(worker);
  const name = workerDisplayName(worker);
  return (
    <>
      <div className="text-sm font-medium text-gray-900">{name}</div>
      <WorkerSecondaryLines meta={meta} />
    </>
  );
};

const IntegrationSourceLine = ({ source, hasSelection }) => {
  const absent = isSourceLabelAbsent(source);
  const tone = absent
    ? hasSelection
      ? "text-white/55 italic"
      : "text-gray-400 italic"
    : hasSelection
      ? "text-white/85"
      : "text-gray-500";
  return (
    <div className={`text-xs ${tone}`}>
      {absent ? "Source non renseignée" : formatSectorSourceLabel(source)}
    </div>
  );
};

const SectorOptionLabelNode = ({ sector }) => (
  <>
    <div className="text-sm font-medium text-gray-900">{sector.name}</div>
    <IntegrationSourceLine source={sector.source} />
  </>
);

const QualificationOptionLabelNode = ({ qualification }) => (
  <>
    <div className="text-sm font-medium text-gray-900">{qualification.name}</div>
    <IntegrationSourceLine source={qualification.source} />
  </>
);

const workerSearchBlob = (worker) => {
  const ids = worker.bigCompanyExternalIds || [];
  const externalBits = ids.flatMap((x) =>
    [x.peopulseId, x.externalId].filter(Boolean),
  );
  const wf = workfullyMatricule(worker);
  return [
    worker.firstname,
    worker.lastname,
    worker.phone,
    wf,
    ...externalBits,
  ]
    .filter(Boolean)
    .join(" ");
};

const flattenCompanyEmployeesFromInfos = (data) => {
  const flat = [];
  const seen = new Set();
  for (const company of data?.companies || []) {
    for (const employee of company.employees || []) {
      const id = String(employee._id);
      if (!seen.has(id)) {
        seen.add(id);
        flat.push(employee);
      }
    }
  }
  return flat;
};

// Composes the create / edit mission flow. `mission` non-null => edit mode.
const ModalCreateMission = ({ isOpen, onClose, onSaved, mission = null, companyId = null }) => {
  const isEdit = Boolean(mission);
  const [view, setView] = useState("mission"); // "mission" | "createWorker" | "createSector" | "createQualification"
  
  // Mission form state
  const [workerId, setWorkerId] = useState("");
  const [workerOptions, setWorkerOptions] = useState([]);
  const [sectorId, setSectorId] = useState("");
  const [sectors, setSectors] = useState([]);
  const [qualificationId, setQualificationId] = useState("");
  const [qualifications, setQualifications] = useState([]);
  const [interimAgencyId, setInterimAgencyId] = useState("");
  const [interimAgencies, setInterimAgencies] = useState([]);
  const [contractType, setContractType] = useState("interim");
  const [dates, setDates] = useState({ start: null, end: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Create sub-forms state
  const [creatingWorkerName, setCreatingWorkerName] = useState({ firstname: "", lastname: "" });
  const [creatingWorkerPhone, setCreatingWorkerPhone] = useState("");
  const [creatingSectorName, setCreatingSectorName] = useState("");
  const [creatingSectorUserIds, setCreatingSectorUserIds] = useState([]);
  const [sectorEmployees, setSectorEmployees] = useState([]);
  const [creatingQualificationName, setCreatingQualificationName] = useState("");
  const [creatingSubmitting, setCreatingSubmitting] = useState(false);
  const [creatingError, setCreatingError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setView("mission");
    if (mission && mission._id) {
      setWorkerId(mission.idWorker || mission.workerId || "");
      setSectorId(mission.sectorId?._id || mission.sectorId || "");
      setQualificationId(mission.qualificationId?._id || mission.qualificationId || "");
      setInterimAgencyId(mission.interimAgency?.id || mission.interimAgency?._id || "");
      setContractType(mission.contractType || "interim");
      setDates({
        start: mission.dates?.start ? forceDateToUtc0(mission.dates.start) : null,
        end: mission.dates?.end ? forceDateToUtc0(mission.dates.end) : null,
      });
    } else {
      setWorkerId("");
      setSectorId("");
      setQualificationId("");
      setInterimAgencyId("");
      setContractType("interim");
      setDates({ start: null, end: null });
    }
    // Reset create forms
    setCreatingWorkerName({ firstname: "", lastname: "" });
    setCreatingWorkerPhone("");
    setCreatingSectorName("");
    setCreatingSectorUserIds([]);
    setCreatingQualificationName("");
    setCreatingError("");
  }, [isOpen, mission]);

  useEffect(() => {
    if (!isOpen) return;
    ApiDatabase.getSectors(
      {},
      (data) => {
        const wf = data?.grouped?.workfully || [];
        const peo = data?.grouped?.peopulse || [];
        const job = data?.grouped?.jobbiz || [];
        setSectors([...wf, ...peo, ...job]);
      },
      () => {}
    );
    ApiDatabase.getQualifications(
      {},
      (data) => {
        const wf = data?.grouped?.workfully || [];
        const peo = data?.grouped?.peopulse || [];
        const job = data?.grouped?.jobbiz || [];
        setQualifications([...wf, ...peo, ...job]);
      },
      () => {}
    );
    ApiDatabase.getInterimAgencyMissions(
      {},
      (data) => {
        setInterimAgencies(data?.interimAgencies || []);
      },
      () => {}
    );
    ApiDatabase.getWorkers(
      {},
      (data) => setWorkerOptions(data?.workers || []),
      () => setWorkerOptions([])
    );
    ApiDatabase.getCompaniesInfosEmployees(
      {},
      (data) => setSectorEmployees(flattenCompanyEmployeesFromInfos(data)),
      () => setSectorEmployees([])
    );
  }, [isOpen]);

  useEffect(() => {
    setCreatingError("");
  }, [view]);

  useEffect(() => {
    if (contractType !== "interim") {
      setInterimAgencyId("");
    }
    if (contractType === "cdi") {
      setDates((prev) => ({ ...prev, end: null }));
    }
  }, [contractType]);

  const canSubmit = useMemo(
    () => Boolean(workerId && dates.start && (contractType === "cdi" || dates.end)),
    [workerId, dates, contractType]
  );

  const buildPayload = () => ({
    companyId,
    workerId,
    sectorId: sectorId || undefined,
    qualificationId: qualificationId || undefined,
    interimAgencyId: contractType === "interim" ? interimAgencyId || undefined : undefined,
    contractType,
    dateStart: dates.start ? dateToYYYYMMDD(dates.start) : undefined,
    dateEnd:
      contractType === "cdi"
        ? undefined
        : dates.end
          ? dateToYYYYMMDD(dates.end)
          : undefined,
  });

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    const payload = buildPayload();
    const onSuccess = () => {
      setSubmitting(false);
      onSaved?.();
    };
    const onError = (err) => {
      setSubmitting(false);
      setError(
        err?.response?.data?.message ||
          "Impossible d'enregistrer la mission. Vérifiez les informations."
      );
    };
    if (isEdit) {
      ApiDatabase.putUpdateMission({ id: mission._id, ...payload }, onSuccess, onError);
    } else {
      ApiDatabase.postCreateMission(payload, onSuccess, onError);
    }
  };

  // Create handlers
  const handleCreateWorker = () => {
    const phoneOk = (creatingWorkerPhone || "").trim().length > 0;
    if (
      !creatingWorkerName.firstname.trim() ||
      !creatingWorkerName.lastname.trim() ||
      !phoneOk
    ) {
      return;
    }
    setCreatingSubmitting(true);
    setCreatingError("");
    ApiDatabase.postCreateWorker(
      {
        firstname: creatingWorkerName.firstname.trim(),
        lastname: creatingWorkerName.lastname.trim(),
        phone: creatingWorkerPhone,
      },
      (data) => {
        const worker = data?.worker;
        if (worker?._id) {
          setWorkerId(worker._id);
          setWorkerOptions((prev) => {
            const exists = prev.some((w) => String(w._id) === String(worker._id));
            return exists ? prev : [worker, ...prev];
          });
        }
        setCreatingSubmitting(false);
        setView("mission");
        setCreatingWorkerName({ firstname: "", lastname: "" });
        setCreatingWorkerPhone("");
      },
      (err) => {
        setCreatingSubmitting(false);
        setCreatingError(
          err?.response?.data?.message ||
            "Impossible de créer cet employé. Vérifiez les informations saisies."
        );
      }
    );
  };

  const handleCreateSector = () => {
    if (!creatingSectorName.trim()) return;
    setCreatingSubmitting(true);
    setCreatingError("");
    ApiDatabase.postCreateSector(
      { name: creatingSectorName.trim(), userIds: creatingSectorUserIds },
      (data) => {
        const sector = data?.sector;
        if (sector?._id) {
          setSectorId(sector._id);
          setSectors((prev) => {
            const exists = prev.some((s) => String(s._id) === String(sector._id));
            return exists ? prev : [sector, ...prev];
          });
        }
        setCreatingSubmitting(false);
        setView("mission");
        setCreatingSectorName("");
        setCreatingSectorUserIds([]);
      },
      (err) => {
        setCreatingSubmitting(false);
        setCreatingError(
          err?.response?.data?.message ||
            "Impossible de créer ce secteur."
        );
      }
    );
  };

  const handleCreateQualification = () => {
    if (!creatingQualificationName.trim()) return;
    setCreatingSubmitting(true);
    ApiDatabase.postCreateQualification(
      { name: creatingQualificationName.trim() },
      (data) => {
        const qualification = data?.qualification;
        if (qualification?._id) {
          setQualificationId(qualification._id);
          setQualifications((prev) => {
            const exists = prev.some((q) => String(q._id) === String(qualification._id));
            return exists ? prev : [qualification, ...prev];
          });
        }
        setCreatingSubmitting(false);
        setView("mission");
        setCreatingQualificationName("");
      },
      () => setCreatingSubmitting(false)
    );
  };

  const selectedWorker = workerOptions.find(
    (w) => String(w._id) === String(workerId)
  );

  const selectedSector = sectors.find((s) => String(s._id) === String(sectorId));

  const selectedQualification = qualifications.find(
    (q) => String(q._id) === String(qualificationId),
  );

  const selectedWorkerLabel = selectedWorker
    ? workerDisplayName(selectedWorker)
    : "Sélectionner un employé";

  const selectedSectorLabel = selectedSector
    ? selectedSector.name
    : "Sélectionner un secteur";

  const selectedQualificationLabel = selectedQualification
    ? selectedQualification.name
    : "Sélectionner un poste";

  const selectedContractTypeLabel =
    CONTRACT_TYPE_OPTIONS.find((option) => option.value === contractType)?.label ||
    "Sélectionner un type";

  const selectedAgencyLabel =
    interimAgencies.find((a) => String(a._id || a.id) === String(interimAgencyId))
      ?.name || "Sélectionner une agence";

  const canSubmitCreateWorker =
    Boolean(creatingWorkerName.firstname.trim()) &&
    Boolean(creatingWorkerName.lastname.trim()) &&
    (creatingWorkerPhone || "").trim().length > 0;

  const createWorkerFooter = (
    <div
      className="flex items-center gap-1 p-1 cursor-pointer"
      onClick={() => setView("createWorker")}
    >
      <Plus wh={20} color="#374151" />
      Créer un employé
    </div>
  );

  const createSectorFooter = (
    <div
      className="flex items-center gap-1 p-1 cursor-pointer"
      onClick={() => setView("createSector")}
    >
      <Plus wh={20} color="#374151" />
      Créer un secteur
    </div>
  );

  const createQualificationFooter = (
    <div
      className="flex items-center gap-1 p-1 cursor-pointer"
      onClick={() => setView("createQualification")}
    >
      <Plus wh={20} color="#374151" />
      Créer un poste
    </div>
  );

  // View: Create Worker
  if (view === "createWorker") {
    return (
      <Modalstructure isShowing={isOpen}>
        <Modalheader hide={onClose} hideShow>
          Créer un employé
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <StyledInput
                type="text"
                label="Prénom"
                placeholder="Prénom"
                value={creatingWorkerName.firstname}
                onChange={(e) =>
                  setCreatingWorkerName((prev) => ({
                    ...prev,
                    firstname: e.target.value,
                  }))
                }
              />
              <StyledInput
                type="text"
                label="Nom"
                placeholder="Nom"
                value={creatingWorkerName.lastname}
                onChange={(e) =>
                  setCreatingWorkerName((prev) => ({
                    ...prev,
                    lastname: e.target.value,
                  }))
                }
              />
            </div>
            <label>
              <div className="ft-sm font-medium text-gray-500">
                Numéro de téléphone
              </div>
              <SelectPhone
                phoneValue={creatingWorkerPhone}
                setPhoneValue={setCreatingWorkerPhone}
              />
            </label>
            {creatingError ? (
              <div className="ft-sm text-red-600">{creatingError}</div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex w-full items-center justify-between">
            <Button color="SECONDARY" size="LG" onClick={() => setView("mission")}>
              <div className="flex items-center gap-1">
                <ChevronLeft wh={20} color="#374151" />
                Retour
              </div>
            </Button>
            <SubmitButton
              loading={creatingSubmitting}
              disabled={!canSubmitCreateWorker}
              onClick={handleCreateWorker}
            />
          </div>
        </ModalFooter>
      </Modalstructure>
    );
  }

  // View: Create Sector
  if (view === "createSector") {
    return (
      <Modalstructure isShowing={isOpen}>
        <Modalheader hide={onClose} hideShow>
          Créer un secteur
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="ft-sm text-gray-500">
              Le secteur peut être créé sans membres ; vous pourrez les ajouter
              ensuite depuis les paramètres.
            </p>
            <StyledInput
              type="text"
              label="Nom du secteur"
              placeholder="Nom du secteur"
              value={creatingSectorName}
              onChange={(e) => setCreatingSectorName(e.target.value)}
            />
            <StyledInput
              label="Utilisateurs autorisés"
              type="selectMultipleCheckbox"
              isSearchable={true}
              value={`${creatingSectorUserIds.length} ${
                creatingSectorUserIds.length === 1 ? "membre" : "membres"
              }`}
              list={sectorEmployees.map((employee) => ({
                value: employee._id,
                label: `${employee.firstname} ${employee.lastname}`.trim(),
                checked: creatingSectorUserIds.some(
                  (id) => String(id) === String(employee._id),
                ),
              }))}
              onChange={(updated) =>
                setCreatingSectorUserIds(
                  updated.filter((opt) => opt.checked).map((opt) => opt.value),
                )
              }
            />
            {creatingError ? (
              <div className="ft-sm text-red-600">{creatingError}</div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex w-full items-center justify-between">
            <Button color="SECONDARY" size="LG" onClick={() => setView("mission")}>
              <div className="flex items-center gap-1">
                <ChevronLeft wh={20} color="#374151" />
                Retour
              </div>
            </Button>
            <SubmitButton
              loading={creatingSubmitting}
              disabled={!creatingSectorName.trim()}
              onClick={handleCreateSector}
            />
          </div>
        </ModalFooter>
      </Modalstructure>
    );
  }

  // View: Create Qualification
  if (view === "createQualification") {
    return (
      <Modalstructure isShowing={isOpen}>
        <Modalheader hide={onClose} hideShow>
          Créer un poste
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <StyledInput
              type="text"
              label="Nom du poste"
              placeholder="Ex : manutentionnaire"
              value={creatingQualificationName}
              onChange={(e) => setCreatingQualificationName(e.target.value)}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex w-full items-center justify-between">
            <Button color="SECONDARY" size="LG" onClick={() => setView("mission")}>
              <div className="flex items-center gap-1">
                <ChevronLeft wh={20} color="#374151" />
                Retour
              </div>
            </Button>
            <SubmitButton
              loading={creatingSubmitting}
              disabled={!creatingQualificationName.trim()}
              onClick={handleCreateQualification}
            />
          </div>
        </ModalFooter>
      </Modalstructure>
    );
  }

  // View: Mission (default)
  return (
    <Modalstructure isShowing={isOpen}>
      <Modalheader hide={onClose} hideShow>
        {isEdit ? "Modifier la mission" : "Créer une mission"}
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <div>
            <StyledInput
              label="Employé"
              type="selectMultipleRadio"
              isSearchable={true}
              value={selectedWorkerLabel}
              list={workerOptions.map((worker) => ({
                value: worker._id,
                label: workerDisplayName(worker),
                labelNode: <WorkerOptionLabelNode worker={worker} />,
                searchText: workerSearchBlob(worker),
                checked: String(worker._id) === String(workerId),
              }))}
              onChange={(updated) => {
                const selected = updated.find((opt) => opt.checked);
                if (selected) {
                  setWorkerId(selected.value);
                }
              }}
              footer={createWorkerFooter}
            />
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <label className="flex flex-col gap-1">
              <div className="text-gray-500">Période</div>
              <RangeDatePicker
                dateFormat="dd/MM/yyyy"
                applyOnSelect={contractType !== "cdi"}
                selectsRange={contractType !== "cdi"}
                onApply={({ start, end }) =>
                  setDates({
                    start,
                    end: contractType === "cdi" ? null : end,
                  })
                }
                startDate={dates.start}
                endDate={dates.end}
                locale="fr"
                peekNextMonth
                dropdownMode="select"
                className="w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-sm"
                placeholderText="Sélectionnez"
              />
            </label>
          </div>

          <StyledInput
            label="Type de contrat"
            type="selectMultipleRadio"
            value={selectedContractTypeLabel}
            list={CONTRACT_TYPE_OPTIONS.map((option) => ({
              ...option,
              checked: option.value === contractType,
            }))}
            onChange={(updated) => {
              const selected = updated.find((opt) => opt.checked);
              if (selected) {
                setContractType(selected.value);
              }
            }}
          />

          <div className="grid grid-cols-2 gap-3">
            <StyledInput
              label="Secteur"
              type="selectMultipleRadio"
              isSearchable={true}
              value={selectedSectorLabel}
              list={sectors.map((sector) => ({
                value: sector._id,
                label: sector.name,
                labelNode: <SectorOptionLabelNode sector={sector} />,
                searchText: `${sector.name} ${formatSectorSourceLabel(sector.source)}`,
                checked: String(sector._id) === String(sectorId),
              }))}
              onChange={(updated) => {
                const selected = updated.find((opt) => opt.checked);
                setSectorId(selected ? selected.value : "");
              }}
              footer={createSectorFooter}
            />
            <StyledInput
              label="Poste"
              type="selectMultipleRadio"
              isSearchable={true}
              value={selectedQualificationLabel}
              list={qualifications.map((qualification) => ({
                value: qualification._id,
                label: qualification.name,
                labelNode: (
                  <QualificationOptionLabelNode qualification={qualification} />
                ),
                searchText: `${qualification.name} ${formatSectorSourceLabel(qualification.source)}`,
                checked: String(qualification._id) === String(qualificationId),
              }))}
              onChange={(updated) => {
                const selected = updated.find((opt) => opt.checked);
                setQualificationId(selected ? selected.value : "");
              }}
              footer={createQualificationFooter}
            />
          </div>

          {contractType === "interim" ? (
            <StyledInput
              label="Agence"
              type="selectMultipleRadio"
              isSearchable={true}
              value={selectedAgencyLabel}
              list={interimAgencies.map((agency) => ({
                value: agency._id,
                label: agency.name,
                checked: String(agency._id) === String(interimAgencyId),
              }))}
              onChange={(updated) => {
                const selected = updated.find((opt) => opt.checked);
                setInterimAgencyId(selected ? selected.value : "");
              }}
            />
          ) : null}

          {error ? <div className="text-sm text-red-600">{error}</div> : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex w-full items-center justify-end gap-2">
          <Button color="SECONDARY" size="LG" onClick={onClose}>
            Annuler
          </Button>
          <SubmitButton
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
          />
        </div>
      </ModalFooter>
    </Modalstructure>
  );
};

export default ModalCreateMission;
